/* eslint-disable complexity */
/* eslint-disable max-statements */
import React, { useState, useEffect, useRef, useCallback, Fragment } from "react";
import PropTypes from "prop-types";
import styles from "./styles.css";
import InputText from "../../shared/input-text";
import debounce from "../../../utils/helpers/debounce";
import { NUMBER, LOCATION_TYPE } from "../../../constants/app-constants";
import filterCities from "../../../utils/helpers/filter-cities";
import groupByKey from "../../../utils/helpers/group-by-key";
import sortByKey from "../../../utils/helpers/sort-by-key";
import splitArray from "../../../utils/helpers/split-array";
import { isValidRouesForPincode } from "./constants";
import { sendCustomGaEvents } from "../../../tracking";
import { appUrl } from "../../../constants/url-constants";
import MapIcon from "./images/maps-and-flags.svg";
import LocatonIcon from "./images/locatonIcon.svg";
import LocatonIconGrey from "./images/locaton-icon-grey.svg";
import searchIcon from "./images/search.svg";
import locationMap from "./images/location-map.png";
import cancelIcon from "./images/cancel.svg";
import { citySelection, locationComponentCta, otherCities, popularCity, searchBarLocation, zeroSearchResults } from "./tracking";
import CityGroupSelector from "../city-group-selector";
import Skeleton from "./skeleton";
import { sanitizeObject } from "../../../utils/helpers/sanitizers";
import parseCityDetails from "../../../utils/parsers/city-details";

const defaultSearchCityLength = NUMBER.TWO;

const LocationPopup = ({
    isLeadForm = false,
    onClickHandler,
    fetchPincodeSearchDetailsConnect,
    onPincodeLocation = () => { },
    geoError,
    parsedCityList,
    selectedCity: { id, name },
    setLocationSelectionTypeConnect,
    route,
    setShowPopularCitiesOnHomepageConnect,
    userId,
    searchCityApiConnect,
    autoFocusSearch,
    openSubcitiesScreen = false,
    setOpenSubcitiesScreen = () => {},
    fetchCityByCityIdConnect = () => {}
}) => {
    const [searchValue, setSearchValue] = useState("");
    const [searchLocationList, setSearchLocationList] = useState([]);
    const [error, setError] = useState(false);
    const [filteredList, setFilteredList] = useState([]);
    const [isC2bLocationPicker, setIsC2bLocationPicker] = useState(false);
    const [defaultList, setDefaultList] = useState([]);
    const cityRef = useRef({});
    const latestSearchValueRef = useRef("");
    const { name: routeName = "" } = route || {};
    const isIndiaHomePage = routeName === "buy-used-cars-home";
    const isBuyerRoute = route.isUsedCarRoute || route.name === "buy-used-cars";

    const getCustomDimension = () => {
        return {
            dimension140: id,
            dimension148: userId
        };
    };

    // Function to process featured cities
    const getFeaturedCities = (filteredListItem) => {
        const [featured] = splitArray(
            sortByKey(filteredListItem, "display_order", "asc"),
            cityList => sanitizeObject(cityList).is_featured
        );
        return featured;
    };

    // Function to process not featured cities
    const getNotFeaturedCities = (filteredListItem) => {
        const [notFeatured] = splitArray(
            sortByKey(filteredListItem, "display_order", "asc"),
            cityList => !sanitizeObject(cityList).is_featured
        );
        return notFeatured;
    };

    // Function to process buyer popular cities
    const getBuyerPopularCities = (filteredListItem) => {
        const [isBuyerPopular] = splitArray(
            sortByKey(filteredListItem, "buyer_display_order", "asc"),
            cityList => sanitizeObject(cityList).is_buy_used_car_popular
        );
        return isBuyerPopular;
    };

    // Function to process buyer not popular cities
    const getBuyerNotPopularCities = (filteredListItem) => {
        const [isBuyerNotPopular] = splitArray(
            sortByKey(filteredListItem, "buyer_display_order", "asc"),
            cityList => !sanitizeObject(cityList).is_buy_used_car_popular
        );
        return isBuyerNotPopular;
    };

    const featured = filteredList.length > 0 ? getFeaturedCities(filteredList) : [];
    const isBuyerPopular = filteredList.length > 0 ? getBuyerPopularCities(filteredList) : [];
    const isBuyerNotPopular = filteredList.length > 0 ? getBuyerNotPopularCities(filteredList) : [];
    const notFeatured = filteredList.length > 0 ? getNotFeaturedCities(filteredList) : [];
    const defaultPopularCity = isBuyerRoute ? isBuyerPopular : featured;
    const defaultOtherCity = isBuyerRoute ? isBuyerNotPopular : notFeatured;
    const [popularCities, setPopularCities] = useState(defaultPopularCity);
    const [searchCity, setSearchCity] = useState("");
    const [notFound, setNotFound] = useState(false);
    const [selectedGroupCityData, setSelectedGroupCityData] = useState(null);
    const [isOnDemandCityLoading, setIsOnDemandCityLoading] = useState({
        isInitialLoading: false,
        isLoading: false
    });
    const [citySearchCount, setCitySearchCount] = useState(0);

    useEffect(() => {
        if (popularCities.length === 0 && searchCity.length === 0) setPopularCities(defaultPopularCity);
        if (defaultList.length === 0 && searchCity.length === 0) setDefaultList(defaultOtherCity);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [featured, notFeatured]);

    useEffect(() => {
        if (parsedCityList.length) {
            setFilteredList(filterCities(parsedCityList));
            // setDefaultList(parsedCityList);
        }
    }, [parsedCityList]);

    useEffect(() => {
        if (!openSubcitiesScreen && selectedGroupCityData) {
            setSelectedGroupCityData(null);
        }
    }, [selectedGroupCityData, openSubcitiesScreen]);

    useEffect(() => {
        setIsC2bLocationPicker(isValidRouesForPincode(routeName || ""));
    }, [routeName]);

    const delayedQuery = useCallback(
        debounce((val) =>
            fetchPincodeSearchDetailsConnect(val)
                .then((response) => {
                    setSearchLocationList(response);
                    setError(false);
                }).catch(() => {
                    setSearchLocationList([]);
                    setError(true);
                })
        , NUMBER.THREE_HUNDRED),
        []
    );

    const onPincodeChange = (e) => {
        const searchedText = e.target.value;
        if (searchedText.length <= NUMBER.SIX) {
            setSearchValue(searchedText);
            if (searchedText && searchedText.length >= NUMBER.THREE) {
                delayedQuery(searchedText);
            } else {
                setSearchLocationList([]);
            }
        }
    };

    useEffect(() => {
        if (searchCity < defaultSearchCityLength) {
            setSearchLocationList([]);
            setFilteredList(filterCities(parsedCityList));
            setPopularCities(defaultPopularCity);
            setDefaultList(defaultOtherCity);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchCity]);

    useEffect(() => {

        if (notFound && searchCity.length > 0) {
            sendCustomGaEvents({
                ...zeroSearchResults,
                eventLabel: searchCity,
                ...getCustomDimension(),
                dimension144: name
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notFound, searchCity]);

    const onClickCityHandler = (cityInfo, e) => {
        const {
            city_slug: citySlug,
            is_seo_city: isSeoCity
        } = cityInfo || {};
        if (e && (!isIndiaHomePage || (isIndiaHomePage && !(isSeoCity && citySlug)))) e.preventDefault();
        setShowPopularCitiesOnHomepageConnect(false);
        setLocationSelectionTypeConnect(LOCATION_TYPE.CITY);
        onPincodeLocation(cityInfo);
        if (searchCity.length > 0) {
            sendCustomGaEvents({
                ...citySelection,
                eventLabel: cityInfo.city_name,
                ...getCustomDimension(),
                dimension144: cityInfo.city_name
            });
        } else {
            sendCustomGaEvents({
                ...popularCity,
                eventLabel: cityInfo.city_name,
                ...getCustomDimension(),
                dimension144: cityInfo.city_name
            });
        }
    };

    const getCitylink = (city) => {
        let redirection;
        const {city_slug: citySlug, is_seo_city: isSeoCity} = city || {};
        if (routeName === "buy-used-cars-home" && citySlug && isSeoCity) {
            redirection = `${appUrl()}/buy-used-cars-${(city.city_slug || "").toLowerCase()}/`;
        } else {
            redirection = "";
        }
        return redirection;
    };

    const getActiveClass = () => {
        let className = "styles.active";
        if (isLeadForm) {
            className = "styles.leadFormCitySelected";
        }
        return className;
    };

    const renderFeatured = () => (
        popularCities.length > 0 && <React.Fragment>
            <label styleName={"styles.label"}>
                Popular cities
            </label>
            <ul styleName={"styles.popularCities"}>
                {
                    popularCities.map(
                        (cityInfo) => {
                            const { group_city: groupCity } = cityInfo || {};
                            return (
                                <li
                                    styleName={id && (id.toString() === cityInfo.city_id) ? getActiveClass() : ""}
                                    key={cityInfo.city_id}
                                    data-title={cityInfo.city_name}>
                                    <a href={groupCity ? "" : getCitylink(cityInfo)} onClick={(e) => {
                                        if (groupCity) {
                                            if (e) {
                                                e.preventDefault();
                                            }
                                            setSelectedGroupCityData(cityInfo);
                                            setOpenSubcitiesScreen(true);
                                        } else {
                                            onClickCityHandler(cityInfo, e);
                                        }
                                    }}>
                                        <span styleName={"styles.cityIcon"}>
                                            <img data-title={cityInfo.city_name} src={cityInfo.realLifeImage} alt={cityInfo.city_name} />
                                        </span>
                                        <h6>{cityInfo.city_name}</h6>
                                    </a>
                                </li>
                            );
                        })
                }
            </ul>
        </React.Fragment>
    );

    const onCityGroupingsClick = (e, cityInfo) => {
        const {
            city_slug: citySlug,
            is_seo_city: isSeoCity
        } = cityInfo;
        if (e && (!isIndiaHomePage || (isIndiaHomePage && !(isSeoCity && citySlug)))) e.preventDefault();
        setLocationSelectionTypeConnect(LOCATION_TYPE.CITY);
        setShowPopularCitiesOnHomepageConnect(false);
        onPincodeLocation(cityInfo);
        sendCustomGaEvents({
            ...otherCities,
            eventLabel: e.target.innerText,
            ...getCustomDimension(),
            dimension144: e.target.innerText
        });

    };

    const renderGroupings = (group) => {

        return group.map(
            (groupItem, index) => {
                const {
                    city_id: cityId,
                    city_name: cityName,
                    group_city: groupCity,
                    is_seo_city: isSeoCity,
                    ...rest
                } = groupItem || {};
                return (
                    <li
                        ref={(el) => { cityRef.current[`${cityId}-${index}`] = el; }}
                        styleName={id && id.toString() === cityId ? "styles.selected styles.cityView" : "styles.cityView"}
                        key={cityId}
                    >
                        <a href={groupCity ? "" : getCitylink({ city_slug: rest.city_slug, is_seo_city: isSeoCity })} onClick={(e) => {
                            if (groupCity) {
                                if (e) e.preventDefault();
                                setSelectedGroupCityData({...groupItem});
                                setOpenSubcitiesScreen(true);
                            } else {onCityGroupingsClick(e, groupItem);}
                        }}>
                            {cityName}
                        </a>
                    </li>
                );
            });
    };

    const renderFinal = (group) => (
        group.map((item, index) => {
            let prefix = "";
            let cityID = null;
            if (item[0]) {
                if (item[0].city_name) {
                    prefix = item[0].city_name.toUpperCase().slice(0, 1);
                }
                if (item[0].city_id) {
                    cityID = item[0].city_id;
                }
            }
            return (
                <Fragment key={index}>
                    <a onClick={() => {
                        if (cityID) {
                            cityRef.current[`${cityID}-0`].scrollIntoView();
                        }
                    }}
                    key={index}>
                        {prefix}
                    </a>
                    <ul styleName={"styles.otherCities"}>
                        {renderGroupings(item)}
                    </ul>
                </Fragment>
            );
        })
    );

    const renderAllCities = () => {
        const updatedDefaultList = [...defaultList];
        const grouping = groupByKey(
            sortByKey(updatedDefaultList, "city_name"),
            "city_name",
        );

        return (
            <div>
                {
                    //hide this label when user is searching
                    defaultList.length > 0 &&
                    <label styleName={"styles.label"}>
                        Other cities
                    </label>
                }
                {
                    !searchValue.length > 0 &&
                    <div className={"container p-0"}>
                        <div styleName={"styles.alphabetWraper"}>
                            {renderFinal(grouping)}
                        </div>
                        {/* <ul styleName={"styles.otherCities"}>
                            {
                                grouping.map((item, index) => {
                                    return (
                                        <div key={index}>
                                            {renderGroupings(item)}
                                        </div>
                                    );
                                })
                            }
                        </ul> */}
                    </div>
                }
                {
                    searchValue.length > 0 &&
                    <div className={"container"}>
                        <ul styleName={"styles.otherCities"}>
                            {renderGroupings(defaultList)}
                        </ul>
                    </div>
                }
            </div>
        );
    };
    const fetchOnDemandCityList = (value) => {
        let filteredPopularArray = [];
        let filteredOtherCitiesArray = [];
        searchCityApiConnect({ searchText: value }).then((cityList) => {
            if (latestSearchValueRef.current.length < defaultSearchCityLength) {
                setIsOnDemandCityLoading((prev) => ({
                    ...prev,
                    isLoading: false,
                    isInitialLoading: false
                }));
                return;
            }

            const filteredCity = parseCityDetails(cityList);
            if (filteredCity.length > 0) {
                if (isBuyerRoute) {
                    filteredPopularArray = getBuyerPopularCities(filteredCity);
                    filteredOtherCitiesArray = getBuyerNotPopularCities(filteredCity);
                } else {
                    filteredPopularArray = getFeaturedCities(filteredCity);
                    filteredOtherCitiesArray = getNotFeaturedCities(filteredCity);
                }
            }
            setPopularCities(filteredPopularArray);
            setDefaultList(filteredOtherCitiesArray);
            setNotFound(!filteredPopularArray.length && !filteredOtherCitiesArray.length);
            setCitySearchCount(filteredCity.length);
            setIsOnDemandCityLoading((prev) => ({
                ...prev,
                isLoading: false,
                isInitialLoading: false
            }));
        });
    };

    const delayedCitySearch = useCallback(
        debounce((val) =>
            fetchOnDemandCityList(val.trim())
        , NUMBER.FIVE_HUNDRED), []);

    const searchByCityInList = (event) => {
        const { value } = event.target;
        setSearchCity(value);
        latestSearchValueRef.current = value;
        if (value && value.trim().length > defaultSearchCityLength) {
            setIsOnDemandCityLoading((prev) => ({
                ...prev,
                isLoading: true,
                ...(citySearchCount === 0 ? { isInitialLoading: true } : {})
            }));
            delayedCitySearch(value);
        } else {
            setCitySearchCount(0);
        }
    };

    const getEventCategory = () => {
        if (routeName === "buy-used-cars") {
            return "Buy_used_cars_B2C_Homepage";
        } else if (routeName === "car-listing") {
            return "Buy_used_cars_B2C_Car_Listing_Page";
        } else if (routeName === "car-detail") {
            return "Buy_used_cars_B2C_Car_Details_Page";
        } else if (routeName === "home") {
            return "Seller_Side";
        } else {
            return "Buyer_Others";
        }
    };

    useEffect(() => {
        sendCustomGaEvents({
            ...locationComponentCta,
            eventCategory: getEventCategory(),
            ...getCustomDimension(),
            dimension144: name
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            if (searchCity) {
                sendCustomGaEvents({
                    ...searchBarLocation,
                    eventLabel: searchCity.charAt(0),
                    ...getCustomDimension(),
                    dimension144: name
                });
            }
        }, NUMBER.FIVE_HUNDRED);

        return () => window.clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchCity]);
    const onClusterCityClick = (e, cityItemData) => {
        const {
            is_buy_used_car_popular: isPopular
        }  = cityItemData || {};
        if (isPopular) {
            onClickCityHandler(cityItemData, e);
        } else {
            onCityGroupingsClick(e, cityItemData);
        }
        setOpenSubcitiesScreen(false);
        setSelectedGroupCityData(null);
    };

    const onBackClick = () => {
        if (openSubcitiesScreen) {
            setOpenSubcitiesScreen(false);
            setSelectedGroupCityData(null);
        }
    };

    return openSubcitiesScreen ?
        <CityGroupSelector
            selectedGroupCityData={selectedGroupCityData}
            fetchCityByCityIdConnect={fetchCityByCityIdConnect}
            onCityClick={onClusterCityClick}
            isUsedCarRoute={route.isUsedCarRoute}
            onBackClick={onBackClick}
        /> : (
            <React.Fragment>
                <div styleName={"styles.header"}>
                    <div styleName="styles.searchInput">
                        <InputText
                            type={isC2bLocationPicker ? "number" : "text"}
                            placeholder ={isC2bLocationPicker ? "Search Pincode" : "Search for your city"}
                            value={searchCity}
                            onChange={isC2bLocationPicker ? onPincodeChange : searchByCityInList}
                            isAutoFocused={autoFocusSearch}
                        />

                        <span styleName={"styles.searchIcon"}>
                            <img src={searchIcon} alt="Search" />
                        </span>
                        {searchCity.length > 0 && !isOnDemandCityLoading.isLoading && <span styleName={"styles.close styles.flexCenter"}>
                            <img src={cancelIcon} alt="Close" onClick={() => setSearchCity("")} />

                        </span>}
                        {isOnDemandCityLoading.isLoading && <span styleName={"styles.loader"} />}
                    </div>
                    <div  styleName={"styles.currentLocationWrap"}>
                        {!geoError &&
                        <a href=""onClick={onClickHandler}>
                            <img src={LocatonIcon} alt="location" />
                            <span styleName="styles.currentLocation">Use current location</span>
                        </a>
                        }
                        {
                            geoError &&
                        <div styleName={"styles.unableLocation"}>
                            <h6 styleName="styles.currentLocation">
                                <img src={LocatonIconGrey} alt="location" />
                                Unable to access location
                            </h6>
                            <span>You have previously denied access</span>
                        </div>
                        }
                    </div>
                </div>
                <div>
                    {
                        isC2bLocationPicker ?
                            <React.Fragment>
                                {error && <p styleName="styles.notFound">We couldn’t find matching locations</p>}
                                <div styleName="styles.innerContainerWrap">
                                    {/* This section is for Input suggestions */}
                                    {searchLocationList && searchLocationList.map((data, index) => (
                                        <div key={index} className="media" styleName="styles.suggestionWrap" onClick={() => onPincodeLocation(data)}>
                                            <img className="mr-3" src={MapIcon} alt="Generic placeholder image" />
                                            <div className="media-body">
                                                <p styleName="styles.pinCode">{data.pincode}</p>
                                                <p styleName="styles.address">
                                                    {data.officename}, {data.city_name}
                                                </p>
                                            </div>
                                        </div>
                                    ))}

                                    {/* This section is for Input suggestions */}
                                </div>
                            </React.Fragment>
                            :
                            <React.Fragment>
                                {isOnDemandCityLoading.isInitialLoading ? (
                                    <Skeleton />
                                ) : (
                                    <div styleName={"styles.outer styles.customScroll"}>
                                        {
                                            (featured.length > 0) && renderFeatured()
                                        }
                                        {
                                            notFound && searchCity.length > 0 &&
                                        <div styleName={"styles.notFoundError"}>
                                            <div styleName={"styles.mapImage"}>
                                                <img src={locationMap} alt="" />
                                            </div>
                                            <p>We couldn’t find your city. <br /> Please share your location to find your nearest servicable area.</p>
                                        </div>
                                        }
                                        {
                                            renderAllCities()
                                        }
                                    </div>
                                )}
                            </React.Fragment>
                    }

                </div>
            </React.Fragment>
        );
};

LocationPopup.propTypes = {
    onClickHandler: PropTypes.func,
    fetchPincodeSearchDetailsConnect: PropTypes.func,
    onPincodeLocation: PropTypes.func,
    geoError: PropTypes.string,
    parsedCityList: PropTypes.array,
    isC2B: PropTypes.bool,
    selectedCity: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string
    }),
    isC2bLocationPicker: PropTypes.func,
    route: PropTypes.object,
    setLocationSelectionTypeConnect: PropTypes.func,
    isLeadForm: PropTypes.bool,
    setShowPopularCitiesOnHomepageConnect: PropTypes.func,
    userId: PropTypes.string,
    setCarListingErrorConnect: PropTypes.func,
    openSubcitiesScreen: PropTypes.bool,
    setOpenSubcitiesScreen: PropTypes.func,
    searchCityApiConnect: PropTypes.func,
    autoFocusSearch: PropTypes.bool,
    fetchCityByCityIdConnect: PropTypes.func
};

export default LocationPopup;
